import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';

import { cn } from '../../lib/cn';
import { colorForClassIndex } from '../../utils/classColors';
import type { BoundingBox, Class as ClassModel } from '../../types/api';

/**
 * BoxPropertiesPanel — bottom section of the right rail.
 *
 * Renders the contextual properties of the currently-selected box:
 *   - Class dropdown (re-classify in place)
 *   - X center / Y center / Width / Height sliders (0..100% with px readout)
 *   - Delete button
 *
 * When no box is selected, shows the hint "Click a box on the canvas to
 * edit it." per the design locked in PIVOTS_2026_05_31.md § 11.
 *
 * Updates are debounced by the caller (useAnnotatorState.updateBox already
 * debounces 300ms), so dragging the slider gives near-live canvas feedback
 * without saturating the network.
 */
interface BoxPropertiesPanelProps {
    isDarkMode?: boolean;
    selectedBox: BoundingBox | null;
    classes: ClassModel[];
    naturalSize?: { w: number; h: number } | null;
    onChange: (id: string, patch: Partial<BoundingBox>) => void;
    onDelete: (id: string) => void;
}

export default function BoxPropertiesPanel({
    isDarkMode = false,
    selectedBox,
    classes,
    naturalSize,
    onChange,
    onDelete,
}: BoxPropertiesPanelProps) {
    const { t } = useTranslation('canvas');

    // Track local slider values so dragging is smooth (avoids re-render
    // bouncing if parent debounces). Sync back to props.box whenever box
    // changes (e.g. server roundtrip lands new values).
    const [local, setLocal] = useState({
        x: selectedBox?.x_center ?? 0,
        y: selectedBox?.y_center ?? 0,
        w: selectedBox?.width ?? 0,
        h: selectedBox?.height ?? 0,
    });

    useEffect(() => {
        if (!selectedBox) return;
        setLocal({
            x: selectedBox.x_center,
            y: selectedBox.y_center,
            w: selectedBox.width,
            h: selectedBox.height,
        });
    }, [selectedBox?.id, selectedBox?.x_center, selectedBox?.y_center, selectedBox?.width, selectedBox?.height]);

    const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';

    const handleSliderChange = (key: 'x' | 'y' | 'w' | 'h', value: number) => {
        if (!selectedBox) return;
        // Clamp + enforce minimum visible width/height
        const v = Math.max(0, Math.min(1, value));
        const next = { ...local, [key]: v };
        // Ensure box stays in [0,1] when w/h grow near edges.
        if (key === 'w') {
            const half = v / 2;
            next.x = Math.max(half, Math.min(1 - half, next.x));
        } else if (key === 'h') {
            const half = v / 2;
            next.y = Math.max(half, Math.min(1 - half, next.y));
        } else if (key === 'x') {
            const half = next.w / 2;
            next.x = Math.max(half, Math.min(1 - half, v));
        } else if (key === 'y') {
            const half = next.h / 2;
            next.y = Math.max(half, Math.min(1 - half, v));
        }
        setLocal(next);
        onChange(selectedBox.id, {
            x_center: next.x,
            y_center: next.y,
            width: next.w,
            height: next.h,
        });
    };

    const handleClassChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (!selectedBox) return;
        const newIdx = Number(e.target.value);
        onChange(selectedBox.id, { class_index: newIdx });
    };

    // Label field — debounced PATCH so each keystroke doesn't fire a request.
    const [labelDraft, setLabelDraft] = useState(selectedBox?.label ?? '');
    useEffect(() => {
        setLabelDraft(selectedBox?.label ?? '');
    }, [selectedBox?.id, selectedBox?.label]);
    const labelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!selectedBox) return;
        const v = e.target.value;
        setLabelDraft(v);
        if (labelTimerRef.current) clearTimeout(labelTimerRef.current);
        labelTimerRef.current = setTimeout(() => {
            // Empty string is the clear-to-NULL signal per the backend contract.
            onChange(selectedBox.id, { label: v });
        }, 400);
    };
    useEffect(() => () => {
        if (labelTimerRef.current) clearTimeout(labelTimerRef.current);
    }, []);

    const pxReadout = useMemo(() => {
        if (!naturalSize) return null;
        return {
            x: Math.round(local.x * naturalSize.w),
            y: Math.round(local.y * naturalSize.h),
            w: Math.round(local.w * naturalSize.w),
            h: Math.round(local.h * naturalSize.h),
        };
    }, [local, naturalSize]);

    return (
        <section className={cn('flex flex-col h-full min-h-0', isDarkMode ? 'bg-gray-900' : 'bg-white')}>
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
                {!selectedBox ? (
                    <div className={cn('text-sm py-6 text-center', muted)}>
                        {t('select_box_to_edit', { defaultValue: 'Click a box on the canvas to edit it.' })}
                    </div>
                ) : (
                    <div className="space-y-4">
                            {/* Class dropdown */}
                            <div>
                                <label className={cn('block text-[11px] font-bold uppercase tracking-wider mb-1.5', muted)}>
                                    {t('class', { defaultValue: 'Class' })}
                                </label>
                                <div className="flex items-center gap-2">
                                    <span
                                        className="h-4 w-4 rounded shrink-0 border"
                                        style={{
                                            backgroundColor:
                                                classes.find((c) => c.class_index === selectedBox.class_index)?.color ||
                                                colorForClassIndex(selectedBox.class_index),
                                            borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                                        }}
                                        aria-hidden="true"
                                    />
                                    <select
                                        value={selectedBox.class_index}
                                        onChange={handleClassChange}
                                        className={cn(
                                            'flex-1 px-3 py-2 rounded-md border text-[13px] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                                            isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900',
                                        )}
                                    >
                                        {classes.map((c) => (
                                            <option key={c.class_index} value={c.class_index}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Custom label — optional free-text tag for THIS box. Empty
                                value clears the label on the server side. Debounced 400ms
                                so typing doesn't fire a PATCH every keystroke. */}
                            <div>
                                <label className={cn('block text-[11px] font-bold uppercase tracking-wider mb-1.5', muted)}>
                                    {t('label_optional', { defaultValue: 'Label (optional)' })}
                                </label>
                                <input
                                    type="text"
                                    value={labelDraft}
                                    onChange={handleLabelChange}
                                    maxLength={64}
                                    placeholder={t('label_placeholder', { defaultValue: 'e.g. person_1' })}
                                    className={cn(
                                        'w-full px-3 py-2 rounded-md border text-[13px] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                                        isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
                                    )}
                                />
                                <p className={cn('text-[11px] mt-1', muted)}>
                                    {t('label_hint', { defaultValue: 'For tagging this specific object (e.g. across multiple images).' })}
                                </p>
                            </div>

                            {/* Sliders */}
                            <SliderRow
                                label={t('x_center', { defaultValue: 'X center' })}
                                value={local.x}
                                onChange={(v) => handleSliderChange('x', v)}
                                px={pxReadout?.x}
                                isDarkMode={isDarkMode}
                            />
                            <SliderRow
                                label={t('y_center', { defaultValue: 'Y center' })}
                                value={local.y}
                                onChange={(v) => handleSliderChange('y', v)}
                                px={pxReadout?.y}
                                isDarkMode={isDarkMode}
                            />
                            <SliderRow
                                label={t('width', { defaultValue: 'Width' })}
                                value={local.w}
                                onChange={(v) => handleSliderChange('w', v)}
                                px={pxReadout?.w}
                                isDarkMode={isDarkMode}
                                min={0.005}
                            />
                            <SliderRow
                                label={t('height', { defaultValue: 'Height' })}
                                value={local.h}
                                onChange={(v) => handleSliderChange('h', v)}
                                px={pxReadout?.h}
                                isDarkMode={isDarkMode}
                                min={0.005}
                            />

                            {/* Delete button */}
                            <button
                                type="button"
                                onClick={() => onDelete(selectedBox.id)}
                                className={cn(
                                    'w-full flex items-center justify-center gap-2 px-4 py-2 mt-2 rounded-lg border text-[13px] font-semibold transition-colors',
                                    isDarkMode
                                        ? 'border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20'
                                        : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100',
                                )}
                            >
                                <Trash2 className="h-4 w-4" />
                                {t('delete_box', { defaultValue: 'Delete box' })}
                            </button>

                        {/* Keyboard hint */}
                        <p className={cn('text-[11px] text-center mt-1', muted)}>
                            {t('delete_hint', { defaultValue: 'Press D or Delete to remove · Ctrl+Z to undo' })}
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
}

interface SliderRowProps {
    label: string;
    value: number; // 0..1 normalized
    px?: number;
    min?: number; // minimum normalized value, default 0
    onChange: (v: number) => void;
    isDarkMode: boolean;
}

function SliderRow({ label, value, px, min = 0, onChange, isDarkMode }: SliderRowProps) {
    const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
    const pct = (value * 100).toFixed(1);
    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <label className={cn('text-[11px] font-bold uppercase tracking-wider', muted)}>
                    {label}
                </label>
                <div className={cn('text-[11px] tabular-nums', muted)}>
                    <span className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>{pct}%</span>
                    {px !== undefined && <span> · {px} px</span>}
                </div>
            </div>
            <input
                type="range"
                min={min}
                max={1}
                step={0.001}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className={cn(
                    'w-full h-1.5 rounded-full appearance-none cursor-pointer',
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-200',
                    '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary-600 [&::-webkit-slider-thumb]:cursor-grab',
                    '[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary-600 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-grab',
                )}
            />
        </div>
    );
}
